import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, MessageSquare, Trash2, Send, Paperclip, Pencil, Save, X, Link2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
  { key: "todo", label: "To Do", color: "border-t-slate-400" },
  { key: "in_progress", label: "In Progress", color: "border-t-cyan-400" },
  { key: "review", label: "Review", color: "border-t-violet-400" },
  { key: "completed", label: "Completed", color: "border-t-emerald-400" },
];

const PRIORITY = { high: "bg-red-500/15 text-red-400 border-red-500/30", medium: "bg-amber-500/15 text-amber-400 border-amber-500/30", low: "bg-slate-500/15 text-slate-400 border-slate-500/30" };

const normalizeUrl = (u) => { const s = (u || "").trim(); if (!s) return ""; return /^https?:\/\//i.test(s) ? s : `https://${s}`; };
const linkLabel = (u) => { try { return new URL(u).hostname.replace(/^www\./, "") + new URL(u).pathname.replace(/\/$/, ""); } catch { return u; } };
const parseLinks = (raw) => raw.split(/[\n,]/).map((s) => normalizeUrl(s)).filter(Boolean);

export default function Tasks() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [team, setTeam] = useState([]);
  const filterKey = `taskAssigneeFilter:${user?.email || "anon"}`;
  const [assigneeFilter, setAssigneeFilter] = useState(() => localStorage.getItem(`taskAssigneeFilter:${user?.email || "anon"}`) || "__me__");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [taskCats, setTaskCats] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const attachRef = useRef();
  const [linkText, setLinkText] = useState("");
  const [form, setForm] = useState({ title: "", description: "", assignee: "unassigned", priority: "medium", status: "todo", due_date: "", tags: "", categories: [], links: "" });

  const load = () => {
    api.get("/tasks").then((r) => setTasks(r.data));
    api.get("/team").then((r) => setTeam(r.data));
    api.get("/settings/task-categories").then((r) => setTaskCats(r.data));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { localStorage.setItem(filterKey, assigneeFilter); }, [filterKey, assigneeFilter]);

  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && tasks.length) {
      const t = tasks.find((x) => x.id === openId);
      if (t) setDetail(t);
    }
  }, [tasks, searchParams]);

  const closeDetail = () => {
    setDetail(null);
    if (searchParams.get("open")) { searchParams.delete("open"); setSearchParams(searchParams, { replace: true }); }
  };

  const create = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    const payload = {
      ...form,
      assignee: form.assignee === "unassigned" ? null : form.assignee,
      due_date: form.due_date || null,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      links: parseLinks(form.links),
    };
    await api.post("/tasks", payload);
    toast.success("Task created");
    setOpen(false);
    setForm({ title: "", description: "", assignee: "unassigned", priority: "medium", status: "todo", due_date: "", tags: "", categories: [], links: "" });
    load();
  };

  const move = async (task, status) => {
    try {
      await api.put(`/tasks/${task.id}`, { status });
      load();
      if (detail?.id === task.id) setDetail({ ...detail, status, completed: status === "completed" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update task");
    }
  };

  const toggleComplete = async (task) => {
    const status = task.completed ? "todo" : "completed";
    await move(task, status);
  };

  const remove = async (id) => {
    await api.delete(`/tasks/${id}`);
    toast.success("Task deleted");
    setDetail(null);
    load();
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    const { data } = await api.post(`/tasks/${detail.id}/notes`, { text: noteText });
    setDetail({ ...detail, notes: [...(detail.notes || []), data] });
    setNoteText("");
    load();
  };

  const refreshDetail = async (id) => {
    const { data } = await api.get(`/tasks/${id}`);
    setDetail(data);
    load();
  };
  const startEdit = () => {
    setEditForm({
      title: detail.title, description: detail.description || "",
      assignee: detail.assignee || "unassigned", priority: detail.priority,
      status: detail.status, due_date: detail.due_date || "",
      tags: (detail.tags || []).join(", "), categories: detail.categories || [],
      links: (detail.links || []).join("\n"),
    });
    setEditing(true);
  };
  const saveEdit = async () => {
    try {
      const { data } = await api.put(`/tasks/${detail.id}`, {
        title: editForm.title, description: editForm.description,
        assignee: editForm.assignee === "unassigned" ? "" : editForm.assignee,
        priority: editForm.priority, status: editForm.status,
        due_date: editForm.due_date || null,
        tags: editForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        categories: editForm.categories,
        links: parseLinks(editForm.links),
      });
      toast.success("Task updated");
      setDetail(data);
      setEditing(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    }
  };
  const uploadAttachment = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    await api.post(`/tasks/${detail.id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    toast.success("Attachment added");
    if (attachRef.current) attachRef.current.value = "";
    refreshDetail(detail.id);
  };
  const deleteAttachment = async (attId) => {
    await api.delete(`/tasks/${detail.id}/attachments/${attId}`);
    refreshDetail(detail.id);
  };
  const addLink = async () => {
    const url = normalizeUrl(linkText);
    if (!url) return;
    const next = [...(detail.links || []), url];
    try {
      const { data } = await api.put(`/tasks/${detail.id}`, { links: next });
      setDetail(data);
      setLinkText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add link");
    }
  };
  const removeLink = async (url) => {
    const next = (detail.links || []).filter((l) => l !== url);
    try {
      const { data } = await api.put(`/tasks/${detail.id}`, { links: next });
      setDetail(data);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove link");
    }
  };
  const toggleFormCat = (name) => setForm((f) => ({ ...f, categories: f.categories.includes(name) ? f.categories.filter((c) => c !== name) : [...f.categories, name] }));
  const toggleEditCat = (name) => setEditForm((f) => ({ ...f, categories: f.categories.includes(name) ? f.categories.filter((c) => c !== name) : [...f.categories, name] }));
  const catColor = (name) => (taskCats.find((c) => c.name === name) || {}).color || "#8B5CF6";
  const canEdit = detail && (user?.role === "admin" || detail.created_by === user?.name || detail.assignee === user?.name);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="task-board-container">
      <PageHeader title="Tasks" subtitle="Plan, assign and track production work.">
        <Button onClick={() => { setForm((f) => ({ ...f, assignee: user?.name || "unassigned" })); setOpen(true); }} data-testid="add-task-button" className="font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> New Task
        </Button>
      </PageHeader>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Assignee</span>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="w-52" data-testid="task-assignee-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Everyone</SelectItem>
            <SelectItem value="__me__">Assigned to me</SelectItem>
            <SelectItem value="__unassigned__">Unassigned</SelectItem>
            {team.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {COLUMNS.map((col) => {
          const items = tasks.filter((t) => t.status === col.key).filter((t) =>
            assigneeFilter === "all" ? true :
            assigneeFilter === "__me__" ? t.assignee === user?.name :
            assigneeFilter === "__unassigned__" ? !t.assignee :
            t.assignee === assigneeFilter
          );
          return (
            <div key={col.key} className={`glass border border-border border-t-2 ${col.color} rounded-2xl p-4`} data-testid={`task-column-${col.key}`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-sm">{col.label}</h3>
                <span className="text-xs font-mono text-muted-foreground bg-secondary/60 px-2 py-0.5 rounded-full">{items.length}</span>
              </div>
              <div className="space-y-3">
                {items.map((t) => (
                  <div key={t.id} onClick={() => setDetail(t)} data-testid="task-card-item"
                    className="group bg-secondary/40 border border-border rounded-xl p-3.5 cursor-pointer hover:border-primary/40 hover:bg-secondary/70 transition-all duration-200">
                    <div className="flex items-start gap-2">
                      <Checkbox checked={t.completed} onCheckedChange={() => toggleComplete(t)} onClick={(e) => e.stopPropagation()}
                        data-testid="task-complete-checkbox" className="mt-0.5" />
                      <p className={`text-sm font-medium flex-1 ${t.completed ? "line-through text-muted-foreground" : ""}`}>{t.title}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2.5 pl-6">
                      <Badge variant="outline" className={`text-[10px] ${PRIORITY[t.priority]}`}>{t.priority}</Badge>
                      {(t.categories || []).map((c) => (
                        <Badge key={c} variant="outline" className="text-[10px]" style={{ borderColor: catColor(c), color: catColor(c) }} data-testid="task-card-category">{c}</Badge>
                      ))}
                      {(t.tags || []).slice(0, 2).map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-[10px]">#{tag}</Badge>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-2.5 pl-6 text-xs text-muted-foreground">
                      <div className="flex items-center gap-2">
                        {t.assignee ? (
                          <span className="flex items-center gap-1" data-testid="task-card-assignee">
                            <span className="w-4 h-4 rounded-full bg-gradient-to-br from-primary to-amber-600 flex items-center justify-center text-[8px] font-bold text-primary-foreground">
                              {t.assignee.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
                            </span>
                            {t.assignee.split(" ")[0]}
                          </span>
                        ) : <span className="italic opacity-60">Unassigned</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        {t.due_date && <span>{new Date(t.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>}
                        {(t.links || []).length > 0 && <span className="flex items-center gap-1" data-testid="task-card-links"><Link2 className="w-3 h-3" />{t.links.length}</span>}
                        {(t.notes || []).length > 0 && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{t.notes.length}</span>}
                      </div>
                    </div>
                  </div>
                ))}
                {items.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No tasks</p>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-border">
          <DialogHeader><DialogTitle className="font-display">New Task</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" placeholder="What needs doing?" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Details..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Assignee</Label>
                <Select value={form.assignee} onValueChange={(v) => setForm({ ...form, assignee: v })}>
                  <SelectTrigger data-testid="task-assignee-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{team.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Due date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Tags (comma separated)</Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="editing, urgent" /></div>
            <div className="space-y-2"><Label>Links (one per line)</Label><Textarea value={form.links} onChange={(e) => setForm({ ...form, links: e.target.value })} data-testid="task-links-input" placeholder="https://docs.google.com/...&#10;https://youtube.com/..." /></div>
            <div className="space-y-2"><Label>Categories</Label>
              <div className="flex flex-wrap gap-1.5">
                {taskCats.length === 0 && <span className="text-xs text-muted-foreground">Add task categories in Settings.</span>}
                {taskCats.map((c) => {
                  const on = form.categories.includes(c.name);
                  return <button key={c.id} type="button" onClick={() => toggleFormCat(c.name)} data-testid={`task-cat-toggle-${c.name}`}
                    className="text-xs px-2.5 py-1 rounded-full border transition-all"
                    style={on ? { background: c.color, borderColor: c.color, color: "#0b0e14" } : { borderColor: c.color, color: c.color }}>{c.name}</button>;
                })}
              </div>
            </div>
          </div>
          <DialogFooter><Button onClick={create} data-testid="task-create-submit" className="font-semibold">Create Task</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setEditing(false); closeDetail(); } }}>
        <DialogContent className="glass border-border max-w-lg max-h-[90vh] overflow-y-auto">
          {detail && !editing && (
            <>
              <DialogHeader><DialogTitle className="font-display pr-8">{detail.title}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                {detail.description && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{detail.description}</p>}
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className={PRIORITY[detail.priority]}>{detail.priority}</Badge>
                  {detail.assignee ? <Badge variant="secondary">@{detail.assignee}</Badge> : <Badge variant="secondary" className="italic opacity-70">Unassigned</Badge>}
                  {detail.due_date && <Badge variant="secondary">{new Date(detail.due_date).toLocaleDateString()}</Badge>}
                  {(detail.categories || []).map((c) => <Badge key={c} variant="outline" style={{ borderColor: catColor(c), color: catColor(c) }} data-testid="task-category-badge">{c}</Badge>)}
                  {(detail.tags || []).map((t) => <Badge key={t} variant="secondary" className="opacity-80">#{t}</Badge>)}
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={detail.status} onValueChange={(v) => move(detail, v)}>
                    <SelectTrigger data-testid="task-detail-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="flex items-center gap-1.5 mb-2"><Paperclip className="w-4 h-4" /> Attachments</Label>
                  {(detail.attachments || []).length > 0 && (
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      {detail.attachments.map((a) => (
                        <div key={a.id} className="group relative bg-secondary/50 rounded-lg overflow-hidden border border-border" data-testid="task-attachment-item">
                          {a.is_image ? (
                            <img src={`${API}/tasks/${detail.id}/attachments/${a.id}`} alt={a.filename} className="w-full h-20 object-cover" />
                          ) : (
                            <div className="h-20 flex items-center justify-center"><Paperclip className="w-5 h-5 text-muted-foreground" /></div>
                          )}
                          <div className="p-1.5 flex items-center gap-1">
                            <a href={`${API}/tasks/${detail.id}/attachments/${a.id}`} target="_blank" rel="noreferrer" title={a.filename} className="text-[10px] truncate flex-1 hover:text-primary">{a.filename}</a>
                            <button onClick={() => deleteAttachment(a.id)} data-testid="task-attachment-delete" className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <input ref={attachRef} type="file" onChange={(e) => uploadAttachment(e.target.files[0])} className="hidden" data-testid="task-attachment-input" />
                  <Button variant="outline" size="sm" onClick={() => attachRef.current?.click()} data-testid="task-attachment-button"><Paperclip className="w-3.5 h-3.5 mr-1.5" /> Attach file or image</Button>
                </div>

                <div>
                  <Label className="flex items-center gap-1.5 mb-2"><Link2 className="w-4 h-4" /> Links</Label>
                  {(detail.links || []).length > 0 && (
                    <div className="space-y-1.5 mb-2" data-testid="task-links-list">
                      {detail.links.map((l) => (
                        <div key={l} className="flex items-center gap-2 bg-secondary/50 rounded-lg px-2.5 py-1.5 text-sm" data-testid="task-link-item">
                          <ExternalLink className="w-3.5 h-3.5 text-primary shrink-0" />
                          <a href={l} target="_blank" rel="noreferrer" className="truncate flex-1 hover:text-primary" title={l}>{linkLabel(l)}</a>
                          {canEdit && <button onClick={() => removeLink(l)} data-testid="task-link-delete" className="text-muted-foreground hover:text-destructive"><X className="w-3.5 h-3.5" /></button>}
                        </div>
                      ))}
                    </div>
                  )}
                  {(detail.links || []).length === 0 && <p className="text-xs text-muted-foreground mb-2">No links yet.</p>}
                  {canEdit && (
                    <div className="flex gap-2">
                      <Input value={linkText} onChange={(e) => setLinkText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLink()}
                        placeholder="Paste a URL..." data-testid="task-link-input" />
                      <Button size="icon" onClick={addLink} data-testid="task-link-add"><Plus className="w-4 h-4" /></Button>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="flex items-center gap-1.5 mb-2"><MessageSquare className="w-4 h-4" /> Notes</Label>
                  <div className="space-y-2 max-h-40 overflow-y-auto mb-3">
                    {(detail.notes || []).length === 0 && <p className="text-xs text-muted-foreground">No notes yet.</p>}
                    {(detail.notes || []).map((n) => (
                      <div key={n.id} className="bg-secondary/50 rounded-lg p-2.5 text-sm">
                        <p className="whitespace-pre-wrap">{n.text}</p>
                        <p className="text-[10px] text-muted-foreground mt-1">{n.author} · {new Date(n.created_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()}
                      placeholder="Add a note..." data-testid="task-note-input" />
                    <Button size="icon" onClick={addNote} data-testid="task-note-submit"><Send className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                {canEdit && <Button variant="outline" onClick={startEdit} data-testid="task-edit-button"><Pencil className="w-4 h-4 mr-1.5" /> Edit</Button>}
                {canEdit && (
                  <Button variant="ghost" onClick={() => remove(detail.id)} data-testid="task-delete-button" className="text-destructive hover:text-destructive">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                )}
                {!canEdit && <p className="text-xs text-muted-foreground self-center">Only the assignee or creator can edit this task.</p>}
              </DialogFooter>
            </>
          )}
          {detail && editing && editForm && (
            <>
              <DialogHeader><DialogTitle className="font-display">Edit Task</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Title</Label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} data-testid="task-edit-title" /></div>
                <div className="space-y-2"><Label>Description</Label><Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Assignee</Label>
                    <Select value={editForm.assignee} onValueChange={(v) => setEditForm({ ...editForm, assignee: v })}>
                      <SelectTrigger data-testid="task-edit-assignee"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="unassigned">Unassigned</SelectItem>{team.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Priority</Label>
                    <Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Status</Label>
                    <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{COLUMNS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>Due date</Label><Input type="date" value={editForm.due_date} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} /></div>
                </div>
                <div className="space-y-2"><Label>Categories</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {taskCats.length === 0 && <span className="text-xs text-muted-foreground">Add task categories in Settings.</span>}
                    {taskCats.map((c) => {
                      const on = editForm.categories.includes(c.name);
                      return <button key={c.id} type="button" onClick={() => toggleEditCat(c.name)} data-testid={`task-edit-cat-${c.name}`}
                        className="text-xs px-2.5 py-1 rounded-full border transition-all"
                        style={on ? { background: c.color, borderColor: c.color, color: "#0b0e14" } : { borderColor: c.color, color: c.color }}>{c.name}</button>;
                    })}
                  </div>
                </div>
                <div className="space-y-2"><Label>Tags (comma separated)</Label><Input value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} /></div>
                <div className="space-y-2"><Label>Links (one per line)</Label><Textarea value={editForm.links} onChange={(e) => setEditForm({ ...editForm, links: e.target.value })} data-testid="task-edit-links" placeholder="https://..." /></div>
              </div>
              <DialogFooter className="gap-2">
                <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={saveEdit} data-testid="task-edit-save" className="font-semibold"><Save className="w-4 h-4 mr-1.5" /> Save</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
